import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      // Not signed in — expected on public pages
      console.debug("auth: not signed in", err?.response?.status);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) {
      try {
        localStorage.setItem("aura_token", data.token);
      } catch (e) {
        console.warn("localStorage unavailable", e);
      }
    }
    await refreshUser();
    return data;
  }, [refreshUser]);

  const register = useCallback(async (name, email, password, referralCode = null) => {
    const payload = { name, email, password };
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await api.post("/auth/register", payload);
    if (data.token) {
      try {
        localStorage.setItem("aura_token", data.token);
      } catch (e) {
        console.warn("localStorage unavailable", e);
      }
    }
    await refreshUser();
    return data;
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout request failed", e);
    }
    try {
      localStorage.removeItem("aura_token");
    } catch (e) {
      console.warn("localStorage unavailable", e);
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
